import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { Heading } from '@/components/heading';
import { SchedulesNoResults } from '@/components/modules/schedules/no-results';
import { SegmentSchedules } from '@/components/route-segment-panel/segment-schedules';
import { Text } from '@/components/text';
import { useSchedules } from '@/hooks/use-schedules';
import { OptionModel } from '@/lib/models/option/types';
import { getOptionTransferLocationIds } from '@/lib/models/option/utils/get-option-transfer-location-ids';
import { getLocationByIdState } from '@/state/locations';
import { getScheduleQueryByIdState, scheduleFamilyState } from '@/state/schedules';
import { ScheduleRequestOceanLineQuery } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { getPreferredIdentifier } from '@/utils/helpers';
import { tv } from '@/utils/styles';

import { OceanServicesSkeleton } from './loading-skeleton';

const generateLineBaseScheduleQuery = (option: OptionModel): ScheduleRequestOceanLineQuery[] => {
  const transferLocations = getOptionTransferLocationIds(option);
  return [
    {
      ids: option.lineIds,
      transfers: transferLocations,
    },
  ];
};

export interface OceanServiceSchedulesProps {
  option: OptionModel;
  originId: string;
  destinationId: string;
  mode: SegmentModeType;
}

const OceanServiceSchedules = ({ option, mode, originId, destinationId }: OceanServiceSchedulesProps) => {
  const [shouldSearch, setShouldSearch] = useState(true);

  const searchKey = option.id;
  const origin = useRecoilValue(getLocationByIdState(originId));
  const destination = useRecoilValue(getLocationByIdState(destinationId));
  const schedules = useRecoilValue(scheduleFamilyState(searchKey));
  const schedulesQuery = useRecoilValue(getScheduleQueryByIdState(searchKey));

  const { performScheduleSearch } = useSchedules({ scheduleKey: searchKey, shouldUseLimited: true });

  const { isSearching: isSearchingSchedules, hasSearched, dateRange } = schedulesQuery;
  const hasSchedules = schedules?.length > 0;

  const lineQuery = generateLineBaseScheduleQuery(option);
  const originIdentifier = getPreferredIdentifier(origin.identifiers)?.value || '';
  const destinationIdentifier = getPreferredIdentifier(destination.identifiers)?.value || '';

  const handleSearch = (wasUserInitiated = true) => {
    performScheduleSearch({
      fromCode: originIdentifier,
      toCode: destinationIdentifier,
      lineQuery,
      mode,
      wasUserInitiated,
      trackingId: 'Option Details',
    });
  };

  useEffect(() => {
    if (shouldSearch) {
      handleSearch(false);
      setShouldSearch(false);
    }
  }, [shouldSearch]);

  const { base, wrapper, headingText, header, subHeadingText } = styles();

  return (
    <div className={base()}>
      <div className={header()}>
        <Heading variant="h2" className={headingText()}>
          Next Departures
        </Heading>
        <Text variant="secondary" className={subHeadingText()}>
          Next scheduled services between {origin.name} and {destination.name}
        </Text>
      </div>
      <div className={wrapper()}>
        {isSearchingSchedules && <OceanServicesSkeleton />}
        {!isSearchingSchedules && hasSchedules && (
          <SegmentSchedules
            schedules={schedules}
            mode={mode}
            customDateRange={undefined}
            direction={dateRange?.to ? 'arrive' : 'depart'}
          />
        )}
      </div>
      {hasSearched && !hasSchedules && !isSearchingSchedules && (
        <SchedulesNoResults
          message="We could note find any upcoming scheduled services on this route."
          secondaryMessage="Please try a different route. If this issue persists - let us know via the feedback button on this page."
        />
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'mt-2 min-h-[30vh] border-t border-grey-200 bg-white py-8',
    header: 'mb-6 max-w-[760px] px-8',
    headingText: 'm-0 mb-1 !text-md leading-tight',
    subHeadingText: 'text-grey-600',
    wrapper: 'px-2',
  },
});

export { OceanServiceSchedules };
